/**
 * /belanja — hub Belanja 3 section (plan 19 Fase 1, Task 1.6 Step 2).
 *
 * Sections:
 *   1. Saldo point (CreditBalanceCard — live dari GET /v1/billing/wallet)
 *   2. Calistung 30 Hari  → Fase 4 (SegeraHadirModal)
 *   3. Versi Cetak        → Fase 5 (SegeraHadirModal)
 *
 * Guest yang menap aksi berbayar (top up) → SegeraHadirModal varian login.
 * Wallet tetap bisa dilihat guest (saldo 0 selama trial).
 */

import { useCallback, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect, useRouter } from 'expo-router';

import { AppIcon } from '@/components/app-icon';
import { CreditBalanceCard } from '@/components/belanja/CreditBalanceCard';
import { MainPageHeader } from '@/components/main/MainPageHeader';
import { SegeraHadirModal } from '@/components/premium/SegeraHadirModal';
import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Colors, MaxContentWidth, Radii, Spacing } from '@/constants/theme';
import { ApiError } from '@/lib/api/client';
import { getWallet, type WalletState } from '@/lib/api/billing';
import { isGuestSession } from '@/lib/auth';
import { MISC_ICON } from '@/lib/icons';

type WalletView = { kind: 'loading' } | { kind: 'ready'; wallet: WalletState } | { kind: 'error' };

export default function BelanjaHubScreen() {
  const router = useRouter();
  const [wallet, setWallet] = useState<WalletView>({ kind: 'loading' });
  const [modal, setModal] = useState<'teaser' | 'login' | null>(null);
  const firstFocusRef = useRef(true);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      // Refresh tiap kembali ke tab (mis. selesai top-up); bukan hanya mount awal.
      const silent = !firstFocusRef.current;
      firstFocusRef.current = false;
      if (!silent) setWallet({ kind: 'loading' });
      getWallet()
        .then((w) => {
          if (!cancelled) setWallet({ kind: 'ready', wallet: w });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          // 401 (sesi dibuang apiFetch) → tampilkan saldo kosong, bukan error.
          if (err instanceof ApiError && err.status === 401) {
            setWallet({ kind: 'ready', wallet: { balance: 0, premium: false } });
            return;
          }
          setWallet({ kind: 'error' });
        });
      return () => {
        cancelled = true;
      };
    }, []),
  );

  const onPressTopUp = useCallback(() => {
    if (isGuestSession()) {
      setModal('login');
      return;
    }
    router.push('/belanja/top-up' as never);
  }, [router]);

  const onPressHistory = useCallback(() => {
    router.push('/belanja/transaksi' as never);
  }, [router]);

  const onPressLogin = useCallback(() => {
    setModal(null);
    router.replace('/login' as never);
  }, [router]);

  const ready = wallet.kind === 'ready' ? wallet.wallet : null;

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.container}>
            <MainPageHeader
              title="Belanja"
              subtitle="Top up point dan temani aktivitas anak."
              right={(
                <View style={styles.headerIcon}>
                  <AppIcon source={MISC_ICON.shoppingBag} size={36} />
                </View>
              )}
            />

            <CreditBalanceCard
              balance={ready ? ready.balance : null}
              onPressTopUp={onPressTopUp}
              onPressHistory={onPressHistory}
            />

            <HubSection
              label="CONTENT PREMIUM"
              title="Aktivitas Calistung 30 Hari"
              description="Aktivitas membaca, menulis, dan berhitung dari ebook Calistung, untuk 3 tingkat usia."
              icon={MISC_ICON.books}
              onPress={() => setModal('teaser')}
            />
            <HubSection
              label="VERSI CETAK"
              title="Bundle aktivitas versi cetak"
              description="Nantikan pilihan bundle aktivitas yang bisa digunakan bersama anak di rumah."
              icon={MISC_ICON.shoppingBag}
              onPress={() => setModal('teaser')}
            />
          </View>
        </ScrollView>
      </SafeAreaView>

      <SegeraHadirModal visible={modal === 'teaser'} onClose={() => setModal(null)} />
      <SegeraHadirModal
        visible={modal === 'login'}
        onClose={() => setModal(null)}
        title="Masuk untuk Mulai Belanja"
        body="Masuk dengan Google dulu untuk top up point dan mulai belanja. Progress Bunda tetap tersimpan."
        ctaLabel="Masuk dengan Google"
        onCtaPress={onPressLogin}
      />
    </ThemedView>
  );
}

function HubSection({
  label,
  title,
  description,
  icon,
  onPress,
}: {
  label: string;
  title: string;
  description: string;
  icon: Parameters<typeof AppIcon>[0]['source'];
  onPress: () => void;
}) {
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <View style={styles.sectionHeaderCopy}>
          <ThemedText type="subtitle" style={styles.sectionTitle}>
            {title}
          </ThemedText>
        </View>
        <View style={styles.soonPill}>
          <ThemedText style={styles.soonPillText}>Segera hadir</ThemedText>
        </View>
      </View>
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [styles.featureCard, pressed && styles.pressed]}
        accessibilityRole="button"
        accessibilityLabel={title}
      >
        <View style={styles.featureIcon}>
          <AppIcon source={icon} size={40} />
        </View>
        <View style={styles.featureCopy}>
          <ThemedText style={styles.featureLabel}>{label}</ThemedText>
          <ThemedText style={styles.featureTitle}>{title}</ThemedText>
          <ThemedText themeColor="textSecondary" style={styles.featureDescription}>
            {description}
          </ThemedText>
        </View>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#fcfbf8' },
  safe: { flex: 1 },
  content: { paddingBottom: Spacing.six },
  container: {
    alignSelf: 'center',
    gap: Spacing.four,
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    width: '100%',
  },
  headerIcon: {
    alignItems: 'center',
    backgroundColor: '#f0e4ff',
    borderRadius: 24,
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
  section: { gap: Spacing.three },
  sectionHeader: { alignItems: 'flex-start', flexDirection: 'row', gap: Spacing.two, justifyContent: 'space-between' },
  sectionHeaderCopy: { flex: 1 },
  sectionTitle: { color: Colors.light.text, fontSize: 21, fontWeight: '700', lineHeight: 28 },
  soonPill: { backgroundColor: 'rgba(86, 63, 122, 0.10)', borderRadius: Radii.pill, paddingHorizontal: Spacing.two, paddingVertical: Spacing.one },
  soonPillText: { color: Brand.primary, fontSize: 10, fontWeight: '700' },
  featureCard: {
    alignItems: 'flex-start',
    backgroundColor: '#ffffff',
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    borderWidth: 1,
    flexDirection: 'row',
    gap: Spacing.three,
    padding: Spacing.three,
  },
  pressed: { opacity: 0.75 },
  featureIcon: { alignItems: 'center', backgroundColor: '#f0e4ff', borderRadius: Radii.medium, height: 62, justifyContent: 'center', width: 62 },
  featureCopy: { flex: 1, gap: Spacing.one },
  featureLabel: { color: Brand.primary, fontSize: 10, fontWeight: '700', letterSpacing: 0.8 },
  featureTitle: { color: Colors.light.text, fontSize: 17, fontWeight: '700', lineHeight: 23 },
  featureDescription: { fontSize: 13, lineHeight: 19 },
});
